import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Checkbox,
  Divider,
  Typography,
  message,
  Popconfirm,
  Spin,
  Empty,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import TeamService, {
  TeamMember,
  RolesMeta,
} from '../../services/team';

const { Text, Paragraph } = Typography;

// ===== 角色中文 + 颜色映射 =====
const ROLE_LABELS: Record<string, string> = {
  owner: '店主',
  admin: '管理员',
  finance: '财务',
  support: '客服',
  viewer: '只读',
};

const ROLE_COLORS: Record<string, string> = {
  owner: 'gold',
  admin: 'blue',
  finance: 'green',
  support: 'cyan',
  viewer: 'default',
};

// 可分配角色（不含 owner）
const ASSIGNABLE_ROLES = ['admin', 'finance', 'support', 'viewer'];

// ===== 状态中文 + 颜色映射 =====
const STATUS_CONFIG: Record<string, { color: string; text: string }> = {
  active: { color: 'success', text: '已加入' },
  invited: { color: 'orange', text: '待接受' },
  suspended: { color: 'default', text: '已停用' },
};

// ===== 16 个权限 scope 中文映射 =====
const PERMISSION_LABELS: Record<string, string> = {
  'payments.view': '查看支付',
  'payments.manage': '管理支付',
  'refunds.view': '查看退款',
  'refunds.manage': '管理退款',
  'disputes.view': '查看争议',
  'disputes.manage': '管理争议',
  'customers.view': '查看客户',
  'customers.manage': '管理客户',
  'subscriptions.view': '查看订阅',
  'subscriptions.manage': '管理订阅',
  'reports.view': '查看报表',
  'treasury.view': '查看资金',
  'treasury.manage': '管理资金',
  'settings.view': '查看设置',
  'settings.manage': '管理设置',
  'team.manage': '管理团队',
};

// 权限分组（用于 Checkbox 分组展示）
const PERMISSION_GROUPS: { label: string; scopes: string[] }[] = [
  { label: '支付', scopes: ['payments.view', 'payments.manage'] },
  { label: '退款', scopes: ['refunds.view', 'refunds.manage'] },
  { label: '争议', scopes: ['disputes.view', 'disputes.manage'] },
  { label: '客户', scopes: ['customers.view', 'customers.manage'] },
  { label: '订阅', scopes: ['subscriptions.view', 'subscriptions.manage'] },
  { label: '报表', scopes: ['reports.view'] },
  { label: '资金', scopes: ['treasury.view', 'treasury.manage'] },
  { label: '设置', scopes: ['settings.view', 'settings.manage'] },
  { label: '团队', scopes: ['team.manage'] },
];

// 矩阵展示用的全部 scope 顺序
const ALL_SCOPES_ORDERED = PERMISSION_GROUPS.flatMap((g) => g.scopes);

const permLabel = (scope: string): string => PERMISSION_LABELS[scope] || scope;
const roleLabel = (role: string): string => ROLE_LABELS[role] || role;

const memberName = (m: TeamMember): string => {
  const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
  return name || '-';
};

const fmtTime = (v?: string): string => (v ? new Date(v).toLocaleString('zh-CN') : '-');

interface PermissionCheckboxesProps {
  value: string[];
  onChange: (next: string[]) => void;
  scopes: string[]; // 全部可选 scope
  disabled?: boolean;
}

// 分组 Checkbox.Group
const PermissionCheckboxes: React.FC<PermissionCheckboxesProps> = ({
  value,
  onChange,
  scopes,
  disabled,
}) => {
  const available = new Set(scopes);
  return (
    <Checkbox.Group
      value={value}
      onChange={(checked) => onChange(checked as string[])}
      style={{ width: '100%' }}
      disabled={disabled}
    >
      {PERMISSION_GROUPS.map((group) => {
        const groupScopes = group.scopes.filter((s) => available.has(s));
        if (groupScopes.length === 0) return null;
        return (
          <div key={group.label} style={{ marginBottom: 12 }}>
            <Text strong>{group.label}</Text>
            <div style={{ marginTop: 6 }}>
              {groupScopes.map((scope) => (
                <Checkbox key={scope} value={scope} style={{ marginRight: 16 }}>
                  {permLabel(scope)}
                </Checkbox>
              ))}
            </div>
          </div>
        );
      })}
    </Checkbox.Group>
  );
};

const TeamTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [rolesMeta, setRolesMeta] = useState<RolesMeta>({ roles: [], permissions: [] });

  // 邀请
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm] = Form.useForm();
  const [inviteRole, setInviteRole] = useState<string>('viewer');
  const [inviteCustom, setInviteCustom] = useState(false);
  const [inviteCustomPerms, setInviteCustomPerms] = useState<string[]>([]);
  const [inviting, setInviting] = useState(false);

  // 编辑
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [editRole, setEditRole] = useState<string>('viewer');
  const [editCustom, setEditCustom] = useState(false);
  const [editCustomPerms, setEditCustomPerms] = useState<string[]>([]);
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // 角色矩阵
  const [matrixOpen, setMatrixOpen] = useState(false);

  // 全部 scope（优先用后端返回，兜底用本地顺序）
  const allScopes = useMemo(
    () => (rolesMeta.permissions.length > 0 ? rolesMeta.permissions : ALL_SCOPES_ORDERED),
    [rolesMeta.permissions]
  );

  // 矩阵展示顺序：以本地分组顺序为准，但只保留后端存在的 scope
  const matrixScopes = useMemo(() => {
    const set = new Set(allScopes);
    const ordered = ALL_SCOPES_ORDERED.filter((s) => set.has(s));
    const extra = allScopes.filter((s) => !ALL_SCOPES_ORDERED.includes(s));
    return [...ordered, ...extra];
  }, [allScopes]);

  // role -> 默认权限映射
  const roleDefaultPerms = useCallback(
    (role: string): string[] => {
      const def = rolesMeta.roles.find((r) => r.role === role);
      return def ? def.permissions : [];
    },
    [rolesMeta.roles]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [list, roles] = await Promise.all([
        TeamService.listMembers(),
        TeamService.getRoles().catch(() => ({ roles: [], permissions: [] } as RolesMeta)),
      ]);
      setMembers(list);
      setRolesMeta(roles);
    } catch (e) {
      message.error('加载团队成员失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ===== 邀请 =====
  const openInvite = () => {
    inviteForm.resetFields();
    inviteForm.setFieldsValue({ role: 'viewer' });
    setInviteRole('viewer');
    setInviteCustom(false);
    setInviteCustomPerms(roleDefaultPerms('viewer'));
    setInviteOpen(true);
  };

  const onInviteRoleChange = (role: string) => {
    setInviteRole(role);
    setInviteCustomPerms(roleDefaultPerms(role));
  };

  const submitInvite = async () => {
    let values: any;
    try {
      values = await inviteForm.validateFields();
    } catch {
      return;
    }
    setInviting(true);
    try {
      const member = await TeamService.inviteMember({
        email: values.email,
        first_name: values.first_name || '',
        last_name: values.last_name || '',
        role: values.role,
        permissions: inviteCustom ? inviteCustomPerms : undefined,
      });
      if (member && member.status === 'active') {
        message.success('已添加成员');
      } else {
        message.success('已发送邀请邮件');
      }
      setInviteOpen(false);
      await loadData();
    } catch (e) {
      message.error('邀请失败，请重试');
    } finally {
      setInviting(false);
    }
  };

  // ===== 编辑 =====
  const openEdit = (m: TeamMember) => {
    setEditing(m);
    editForm.setFieldsValue({
      first_name: m.first_name,
      last_name: m.last_name,
      role: m.role,
    });
    setEditRole(m.role);
    setEditActive(m.active);
    // 若成员已有自定义权限且与角色默认不同，则默认展开自定义
    const defaults = roleDefaultPerms(m.role);
    const hasCustom =
      m.permissions.length > 0 &&
      (m.permissions.length !== defaults.length ||
        m.permissions.some((p) => !defaults.includes(p)));
    setEditCustom(hasCustom);
    setEditCustomPerms(m.permissions.length > 0 ? m.permissions : defaults);
    setEditOpen(true);
  };

  const onEditRoleChange = (role: string) => {
    setEditRole(role);
    if (!editCustom) {
      setEditCustomPerms(roleDefaultPerms(role));
    }
  };

  const submitEdit = async () => {
    if (!editing) return;
    let values: any;
    try {
      values = await editForm.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      await TeamService.updateMember(editing.id, {
        first_name: values.first_name,
        last_name: values.last_name,
        role: values.role,
        permissions: editCustom ? editCustomPerms : roleDefaultPerms(values.role),
        active: editActive,
      });
      message.success('成员已更新');
      setEditOpen(false);
      await loadData();
    } catch (e) {
      message.error('更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // ===== 移除 =====
  const handleRemove = async (m: TeamMember) => {
    try {
      await TeamService.removeMember(m.id);
      message.success('成员已移除');
      await loadData();
    } catch (e) {
      message.error('移除失败，请重试');
    }
  };

  // ===== 列表列 =====
  const columns = [
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '姓名',
      key: 'name',
      render: (_: any, m: TeamMember) => memberName(m),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={ROLE_COLORS[role] || 'default'}>{roleLabel(role)}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const cfg = STATUS_CONFIG[status] || { color: 'default', text: status };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: '最近活跃',
      dataIndex: 'last_active',
      key: 'last_active',
      render: (v: string) => fmtTime(v),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, m: TeamMember) => {
        const isOwner = m.role === 'owner';
        return (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={isOwner}
              onClick={() => openEdit(m)}
            >
              编辑
            </Button>
            <Popconfirm
              title="确定移除该成员吗？"
              onConfirm={() => handleRemove(m)}
              okText="移除"
              cancelText="取消"
              disabled={isOwner}
            >
              <Button size="small" danger icon={<DeleteOutlined />} disabled={isOwner}>
                移除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // ===== 角色权限矩阵列 =====
  const matrixColumns = [
    {
      title: '权限',
      dataIndex: 'scope',
      key: 'scope',
      fixed: 'left' as const,
      width: 140,
      render: (scope: string) => <Text>{permLabel(scope)}</Text>,
    },
    ...ASSIGNABLE_ROLES.map((role) => ({
      title: <Tag color={ROLE_COLORS[role]}>{roleLabel(role)}</Tag>,
      key: role,
      align: 'center' as const,
      render: (_: any, row: { scope: string }) => {
        const perms = roleDefaultPerms(role);
        return perms.includes(row.scope) ? (
          <CheckOutlined style={{ color: '#52c41a' }} />
        ) : (
          <CloseOutlined style={{ color: '#d9d9d9' }} />
        );
      },
    })),
  ];

  const matrixData = matrixScopes.map((scope) => ({ key: scope, scope }));

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Paragraph type="secondary">
        管理团队成员及其访问权限。不同角色拥有不同的操作范围，可在邀请或编辑成员时自定义权限。
      </Paragraph>

      <Space style={{ marginBottom: 16 }} wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={openInvite}>
          邀请成员
        </Button>
        <Button icon={<TeamOutlined />} onClick={() => setMatrixOpen(true)}>
          角色权限矩阵
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadData}>
          刷新
        </Button>
      </Space>

      <Table<TeamMember>
        rowKey="id"
        columns={columns}
        dataSource={members}
        pagination={false}
        locale={{ emptyText: <Empty description="暂无团队成员" /> }}
      />

      {/* ===================== 邀请成员 Modal ===================== */}
      <Modal
        title="邀请成员"
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onOk={submitInvite}
        confirmLoading={inviting}
        okText="发送邀请"
        cancelText="取消"
        width={620}
        destroyOnClose
      >
        <Form form={inviteForm} layout="vertical">
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input placeholder="member@company.com" />
          </Form.Item>
          <Space style={{ width: '100%' }} size={16}>
            <Form.Item name="first_name" label="名" style={{ flex: 1, minWidth: 0 }}>
              <Input placeholder="名" />
            </Form.Item>
            <Form.Item name="last_name" label="姓" style={{ flex: 1, minWidth: 0 }}>
              <Input placeholder="姓" />
            </Form.Item>
          </Space>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select onChange={onInviteRoleChange}>
              {ASSIGNABLE_ROLES.map((role) => (
                <Select.Option key={role} value={role}>
                  {roleLabel(role)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        <Divider style={{ margin: '8px 0 16px' }} />

        <div style={{ marginBottom: 12 }}>
          <Text strong>该角色默认权限</Text>
          <div style={{ marginTop: 8 }}>
            {roleDefaultPerms(inviteRole).length === 0 ? (
              <Text type="secondary">无</Text>
            ) : (
              <Space wrap size={[8, 8]}>
                {roleDefaultPerms(inviteRole).map((scope) => (
                  <Tag key={scope} color="blue">
                    {permLabel(scope)}
                  </Tag>
                ))}
              </Space>
            )}
          </div>
        </div>

        <Checkbox
          checked={inviteCustom}
          onChange={(e) => {
            setInviteCustom(e.target.checked);
            if (e.target.checked) {
              setInviteCustomPerms(roleDefaultPerms(inviteRole));
            }
          }}
        >
          自定义权限
        </Checkbox>

        {inviteCustom && (
          <div style={{ marginTop: 12 }}>
            <Alert
              type="info"
              showIcon
              message="勾选的权限将覆盖该角色的默认权限。"
              style={{ marginBottom: 12 }}
            />
            <PermissionCheckboxes
              value={inviteCustomPerms}
              onChange={setInviteCustomPerms}
              scopes={allScopes}
            />
          </div>
        )}
      </Modal>

      {/* ===================== 编辑成员 Modal ===================== */}
      <Modal
        title={editing ? `编辑成员 - ${editing.email}` : '编辑成员'}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={submitEdit}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        width={620}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Space style={{ width: '100%' }} size={16}>
            <Form.Item name="first_name" label="名" style={{ flex: 1, minWidth: 0 }}>
              <Input placeholder="名" />
            </Form.Item>
            <Form.Item name="last_name" label="姓" style={{ flex: 1, minWidth: 0 }}>
              <Input placeholder="姓" />
            </Form.Item>
          </Space>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select onChange={onEditRoleChange}>
              {ASSIGNABLE_ROLES.map((role) => (
                <Select.Option key={role} value={role}>
                  {roleLabel(role)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="启用状态">
            <Space>
              <Switch
                checked={editActive}
                checkedChildren="启用"
                unCheckedChildren="停用"
                onChange={setEditActive}
              />
              <Text type="secondary">
                {editActive ? '成员可正常访问' : '已停用，成员将无法访问'}
              </Text>
            </Space>
          </Form.Item>
        </Form>

        <Divider style={{ margin: '8px 0 16px' }} />

        <Checkbox
          checked={editCustom}
          onChange={(e) => {
            setEditCustom(e.target.checked);
            if (!e.target.checked) {
              setEditCustomPerms(roleDefaultPerms(editRole));
            }
          }}
        >
          自定义权限
        </Checkbox>

        {!editCustom ? (
          <div style={{ marginTop: 12 }}>
            <Text strong>该角色默认权限</Text>
            <div style={{ marginTop: 8 }}>
              {roleDefaultPerms(editRole).length === 0 ? (
                <Text type="secondary">无</Text>
              ) : (
                <Space wrap size={[8, 8]}>
                  {roleDefaultPerms(editRole).map((scope) => (
                    <Tag key={scope} color="blue">
                      {permLabel(scope)}
                    </Tag>
                  ))}
                </Space>
              )}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <Alert
              type="info"
              showIcon
              message="勾选的权限将覆盖该角色的默认权限。"
              style={{ marginBottom: 12 }}
            />
            <PermissionCheckboxes
              value={editCustomPerms}
              onChange={setEditCustomPerms}
              scopes={allScopes}
            />
          </div>
        )}
      </Modal>

      {/* ===================== 角色权限矩阵 Modal ===================== */}
      <Modal
        title="角色权限矩阵"
        open={matrixOpen}
        onCancel={() => setMatrixOpen(false)}
        footer={
          <Button type="primary" onClick={() => setMatrixOpen(false)}>
            关闭
          </Button>
        }
        width={720}
      >
        <Paragraph type="secondary">
          下表展示各可分配角色的默认权限。店主（owner）默认拥有全部权限，且不可修改。
        </Paragraph>
        <Table
          rowKey="key"
          columns={matrixColumns}
          dataSource={matrixData}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Modal>
    </div>
  );
};

export default TeamTab;
